import React, { useEffect, useMemo, useState } from 'react';
import { Select, DatePicker } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAdminRoleIfNotInLocalStore,
  getAdminRoles,
  getAllEnum,
  getEnums,
  getEventTypes,
  getBuildLocations,
  fetchEventTypes,
} from '../../../features/enum.store';
import {
  fetchMinimalCompanyList,
  getMinimalCompanyList,
  getMinimalCompanyListLoading,
} from '../../../features/company.store';
import { getUser } from '../../../features/auth.store';
import to from '../../../utils/to';
import get from 'lodash.get';
import isUndefined from 'lodash/isUndefined';
import { fetchMinimalSiteList, getMinimalSiteList, getMinimalSiteLoading } from '../../../features/site.store';
import {
  getEventUserList,
  getEventUserPersonList,
  getUsersLoading,
  fetchEventUserList,
  fetchEventUserPersonList,
} from '../../../features/user.store';
import { statusMapper, YesNo, recurrenceTypes, SCHEDULE_VIEW_TYPE, OnSiteOffSite } from '../../../utils/constant';
import moment from 'moment';

const { Option } = Select;
const { RangePicker } = DatePicker;

const normalizeSelectValue = (value) => {
  if (value === null || value === '') return undefined;
  return value;
};

const uniqueBy = (list, getKey) => {
  const map = new Map();
  (list || []).forEach((item) => {
    const key = getKey(item);
    if (key === null || key === undefined || key === '') return;
    if (!map.has(key)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
};

export const CompanySelectDropDown = ({
  onChange,
  selectFirstOnLoad,
  selectFirstIfValueIsEmpty,
  value: controlledValue,
  ...props
}) => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const minimalCompanyList = useSelector(getMinimalCompanyList) || [];
  const minimalCompanyListLoading = useSelector(getMinimalCompanyListLoading);
  const [value, setValue] = useState(normalizeSelectValue(controlledValue));

  const asObject = (item) => (item && typeof item === 'object' ? item : {});

  const getCompanyValue = (item) =>
    (typeof item === 'string' ? item : null) ||
    get(asObject(item), 'identifier') ||
    get(asObject(item), 'tenantIdentifier') ||
    get(asObject(item), 'tenant.id') ||
    get(asObject(item), 'id') ||
    '';

  const getCompanyLabel = (item) =>
    get(asObject(item), 'name') ||
    get(asObject(item), 'tenantName') ||
    get(asObject(item), 'tenant.name') ||
    get(asObject(item), 'companyName') ||
    get(asObject(item), 'displayName') ||
    get(asObject(item), 'label') ||
    get(asObject(item), 'identifier') ||
    get(asObject(item), 'tenantIdentifier') ||
    get(asObject(item), 'id') ||
    (typeof item === 'string' ? item : null) ||
    '';

  const userTenantIdentifier =
    get(user, 'tenant.identifier') ||
    get(user, 'tenant.id') ||
    get(user, 'tenantIdentifier') ||
    get(user, 'tenant_identifier') ||
    '';

  const userTenantName =
    get(user, 'tenant.name') ||
    get(user, 'tenantName') ||
    get(user, 'tenant_name') ||
    get(user, 'companyName') ||
    get(user, 'tenant.displayName') ||
    '';

  const safeCompanies = useMemo(() => {
    const normalized = (minimalCompanyList || []).map((item) => {
      const valueKey = getCompanyValue(item);
      const rawLabel = getCompanyLabel(item);

      let label = rawLabel;
      if (label === valueKey && userTenantName && valueKey && String(valueKey) === String(userTenantIdentifier)) {
        label = userTenantName;
      }
      if (label === valueKey && userTenantName && minimalCompanyList.length === 1) {
        label = userTenantName;
      }

      return {
        ...(asObject(item) || {}),
        valueKey,
        label,
      };
    });

    const deduped = uniqueBy(normalized, (item) => item?.valueKey);
    return deduped.sort((a, b) => String(a?.label || '').localeCompare(String(b?.label || '')));
  }, [minimalCompanyList, userTenantIdentifier, userTenantName]);

  const handleOnChange = (nextValue) => {
    const safeValue = normalizeSelectValue(nextValue);
    setValue(safeValue);
    onChange && onChange(safeValue);
  };

  useEffect(() => {
    setValue(normalizeSelectValue(controlledValue));
  }, [controlledValue]);

  useEffect(() => {
    (async () => {
      await to(dispatch(fetchMinimalCompanyList()).unwrap());
    })();
  }, [dispatch]);

  useEffect(() => {
    if (!safeCompanies.length) return;

    const firstElement = get(safeCompanies, [0, 'valueKey']);

    if (!firstElement) return;

    if (selectFirstOnLoad && !controlledValue) {
      setValue(firstElement);
      onChange && onChange(firstElement);
      return;
    }

    if (
      selectFirstIfValueIsEmpty &&
      (controlledValue === '' || isUndefined(controlledValue) || controlledValue === null)
    ) {
      setValue(firstElement);
      onChange && onChange(firstElement);
    }
  }, [safeCompanies, controlledValue, onChange, selectFirstOnLoad, selectFirstIfValueIsEmpty]);

  return (
    <Select
      showSearch
      optionFilterProp="children"
      className="w-100"
      value={value}
      loading={minimalCompanyListLoading}
      filterOption={(input, option) =>
        String(option?.children || '')
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      }
      onChange={handleOnChange}
      {...props}
    >
      {safeCompanies.map((company) => (
        <Option value={company?.valueKey} key={company?.valueKey}>
          {company?.label || company?.valueKey}
        </Option>
      ))}
    </Select>
  );
};

export const SiteSelectDropdown = ({
  hide,
  company,
  onChange,
  selectFirstOnLoad,
  selectFirstIfValueIsEmpty,
  value: controlledValue,
  ...rest
}) => {
  const dispatch = useDispatch();
  const minimalSiteList = useSelector(getMinimalSiteList) || [];
  const minimalSiteListLoading = useSelector(getMinimalSiteLoading);
  const [value, setValue] = useState(normalizeSelectValue(controlledValue));

  const safeSites = useMemo(() => {
    const deduped = uniqueBy(minimalSiteList, (item) => item?.id);
    return deduped.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [minimalSiteList]);

  const handleOnChange = (nextValue) => {
    const safeValue = normalizeSelectValue(nextValue);
    setValue(safeValue);
    onChange && onChange(safeValue);
  };

  useEffect(() => {
    setValue(normalizeSelectValue(controlledValue));
  }, [controlledValue]);

  useEffect(() => {
    (async () => {
      const [err, data] = await to(dispatch(fetchMinimalSiteList({ company })).unwrap());
      if (err) return;

      const firstElement = get(data, ['list', 0, 'id']);

      if (selectFirstOnLoad) {
        if (firstElement) {
          const nextValue = String(firstElement);
          setValue(nextValue);
          onChange && onChange(nextValue);
        } else {
          setValue(undefined);
          onChange && onChange(undefined);
        }
        return;
      }

      if (
        selectFirstIfValueIsEmpty &&
        (controlledValue === '' || isUndefined(controlledValue) || controlledValue === null)
      ) {
        if (firstElement) {
          const nextValue = String(firstElement);
          setValue(nextValue);
          onChange && onChange(nextValue);
        } else {
          setValue(undefined);
          onChange && onChange(undefined);
        }
      }
    })();
  }, [company, dispatch, selectFirstOnLoad, selectFirstIfValueIsEmpty, controlledValue, onChange]);

  if (hide && hide()) {
    return <></>;
  }

  return (
    <Select
      {...rest}
      onChange={handleOnChange}
      className="w-100"
      showSearch
      value={value}
      loading={minimalSiteListLoading}
      placeholder="Select"
      optionFilterProp="children"
    >
      {safeSites.map(({ name, id }) => (
        <Option value={`${id}`} key={id}>
          {name}
        </Option>
      ))}
    </Select>
  );
};

export const SiteSelectMultiDropdown = ({ company, onChange, ...rest }) => {
  const dispatch = useDispatch();
  const minimalSiteList = useSelector(getMinimalSiteList) || [];
  const minimalSiteListLoading = useSelector(getMinimalSiteLoading);
  const [value, setValue] = useState(rest.value || []);
  const [isLoaded, setIsLoaded] = useState(false);
  const [prevCompany, setPrevCompany] = useState(null);

  useEffect(() => {
    (async () => {
      const [err, data] = await to(dispatch(fetchMinimalSiteList({ company })).unwrap());
      if (!err && !isLoaded) {
        setValue(
          (value || [])
            .map((v) => {
              const f = data.list.find((item) => item.id === v);
              return f && f.name;
            })
            .filter(Boolean),
        );
        setIsLoaded(true);
      }

      if (prevCompany !== null && prevCompany !== company) {
        setValue([]);
        onChange && onChange([]);
      }
      setPrevCompany(company);
    })();
  }, [company, dispatch]); // eslint-disable-line

  const handleOnChange = (selectedValues, optionList) => {
    setValue(selectedValues || []);
    onChange && onChange((optionList || []).map((k) => k.id));
  };

  const safeSites = useMemo(() => {
    const deduped = uniqueBy(minimalSiteList, (item) => item?.id);
    return deduped.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [minimalSiteList]);

  return (
    <Select
      {...rest}
      onChange={handleOnChange}
      className="w-100"
      showSearch
      value={value}
      loading={minimalSiteListLoading}
      placeholder="Select"
      options={safeSites.map((o) => ({
        value: o.name,
        key: o.id,
        id: o.id,
      }))}
    />
  );
};

export const RegionSelectMultiDropdown = ({ onChange, ...rest }) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(rest.value);

  useEffect(() => {
    dispatch(getEnums({ type: 'buildLocations' }));
  }, [dispatch]);

  const buildLocations = useSelector(getBuildLocations);

  const handleOnChange = (nextValue) => {
    setValue(nextValue);
    onChange && onChange(nextValue);
  };

  return (
    <Select {...rest} className="w-100" showSearch value={value} placeholder="Select" onChange={handleOnChange}>
      {buildLocations.map(({ label, value: optionValue }) => (
        <Option value={optionValue} key={optionValue}>
          {label}
        </Option>
      ))}
    </Select>
  );
};

export const AdminRolesDropdown = ({ ...props }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAdminRoleIfNotInLocalStore());
  }, [dispatch]);

  const adminRoles = useSelector(getAdminRoles);

  return (
    <Select {...props}>
      {adminRoles.map(({ displayName, id }) => (
        <Option value={`${id}`} key={id}>
          {displayName}
        </Option>
      ))}
    </Select>
  );
};

export const AvailableUsers = ({ users = [], value, onChange, ...props }) => {
  const safeUsers = useMemo(() => {
    const deduped = uniqueBy(users, (item) => item?.id);
    return deduped.sort((a, b) => String(a?.fullName || '').localeCompare(String(b?.fullName || '')));
  }, [users]);

  return (
    <Select
      {...props}
      value={normalizeSelectValue(value)}
      onChange={(nextValue) => onChange && onChange(normalizeSelectValue(nextValue))}
      showSearch
      optionFilterProp="children"
    >
      {safeUsers.map(({ fullName, id }) => (
        <Option value={`${id}`} key={id}>
          {fullName}
        </Option>
      ))}
    </Select>
  );
};

export const TaskTypesDropDown = ({ onChange, value, viewType, includeAll, selectFirstOnLoad, ...rest }) => {
  const dispatch = useDispatch();
  const eventTypes = useSelector(getEventTypes);

  useEffect(() => {
    dispatch(fetchEventTypes(viewType));
  }, [dispatch, viewType]);

  useEffect(() => {
    if (selectFirstOnLoad && eventTypes.length > 0) {
      onChange && onChange(eventTypes[isUndefined(includeAll) ? 1 : 0].name);
    }
  }, [eventTypes, includeAll, onChange, selectFirstOnLoad]);

  return (
    <Select {...rest} onChange={onChange} className="w-100" value={normalizeSelectValue(value)} loading={false} showSearch>
      {eventTypes
        .filter((event) => (isUndefined(includeAll) && event.name === 'ALL' ? false : event))
        .map(({ displayName, name }) => (
          <Option value={name} key={name}>
            {displayName}
          </Option>
        ))}
    </Select>
  );
};

export const RecurranceTypesDropDown = ({ onChange, value, selectFirstOnLoad, ...rest }) => {
  useEffect(() => {
    if (selectFirstOnLoad) {
      onChange && onChange(recurrenceTypes[0].value);
    }
  }, [onChange, selectFirstOnLoad]);

  return (
    <Select {...rest} onChange={onChange} className="w-100" value={normalizeSelectValue(value)} loading={false} showSearch>
      {recurrenceTypes.map(({ displayName, value: optionValue }) => (
        <Option value={optionValue} key={optionValue}>
          {displayName}
        </Option>
      ))}
    </Select>
  );
};

export const ScheduleStatusDropDown = ({ onChange, value, viewType, selectFirstOnLoad, ...rest }) => {
  useEffect(() => {
    if (selectFirstOnLoad) {
      onChange && onChange(Object.keys(statusMapper[viewType || SCHEDULE_VIEW_TYPE.EMP_SCHEDULE])[0]);
    }
  }, [onChange, selectFirstOnLoad, viewType]);

  return (
    <Select {...rest} onChange={onChange} className="w-100" value={normalizeSelectValue(value)} loading={false} showSearch>
      {Object.keys(statusMapper[viewType || SCHEDULE_VIEW_TYPE.EMP_SCHEDULE]).map((keyValue) => (
        <Option value={keyValue} key={keyValue}>
          {keyValue.replaceAll('_', ' ')}
        </Option>
      ))}
    </Select>
  );
};

export const YesNoDropDown = ({ onChange, value, ...rest }) => {
  useEffect(() => {
    onChange && onChange(YesNo[0].value);
  }, [onChange]);

  return (
    <Select {...rest} onChange={onChange} className="w-100" value={normalizeSelectValue(value)} loading={false}>
      {YesNo.map(({ displayName, value: optionValue }) => (
        <Option value={optionValue} key={optionValue}>
          {displayName}
        </Option>
      ))}
    </Select>
  );
};

export const LocationDropDown = ({ onChange, value, ...rest }) => {
  useEffect(() => {
    onChange && onChange(OnSiteOffSite[0].value);
  }, [onChange]);

  return (
    <Select {...rest} onChange={onChange} className="w-100" value={normalizeSelectValue(value)} loading={false}>
      {OnSiteOffSite.map(({ displayName, value: optionValue }) => (
        <Option value={optionValue} key={optionValue}>
          {displayName}
        </Option>
      ))}
    </Select>
  );
};

export const EventUserMultiSearchDropDown = ({ onChange, viewType, value, person, ...rest }) => {
  const isPerson = !isUndefined(person);
  const dispatch = useDispatch();
  const usersList = isPerson ? useSelector(getEventUserPersonList) : useSelector(getEventUserList);
  const usersListIsLoading = useSelector(getUsersLoading);
  const [selectedItems, setSelectedItems] = useState([]);

  const handleOnSelect = (selectedValue, c) => {
    onChange &&
      onChange([
        ...(value || []),
        {
          id: selectedValue,
          email: c.children,
        },
      ]);
  };

  const handleOnDeSelect = (selectedValue) => {
    onChange && onChange((value || []).filter((v) => v.email !== selectedValue));
  };

  useEffect(() => {
    if (isPerson) dispatch(fetchEventUserPersonList({ viewType }));
    else dispatch(fetchEventUserList({ viewType }));
  }, [dispatch, isPerson, viewType]);

  useEffect(() => {
    value && setSelectedItems(value.map((k) => k.email));
  }, [value]);

  return (
    <Select
      {...rest}
      onDeselect={handleOnDeSelect}
      onSelect={handleOnSelect}
      className="w-100"
      value={selectedItems}
      loading={usersListIsLoading}
      mode="multiple"
      allowClear
      showSearch
    >
      {usersList &&
        usersList
          .filter((v) => (value || []).findIndex((i) => i.id === v.id) === -1)
          .map((user) => (
            <Option value={user.id} key={user.id}>
              {user.email}
            </Option>
          ))}
    </Select>
  );
};

export const DateRangeSelector = ({ onChange, value }) => {
  const handleOnChange = (date) => {
    onChange && onChange(date.map((v) => v.format('YYYY-MM-DD')));
  };
  const dateValues = value && value.map((v) => moment(v, 'YYYY-MM-DD'));
  return <RangePicker allowClear={false} onChange={handleOnChange} value={dateValues} className="w-100" />;
};

////////////////////////////////////////////////////////////////////////////////////////
// form elements for Add Chambers
////////////////////////////////////////////////////////////////////////////////////////

export const CommonDropdown = (props) => <Select showSearch className="w-100" {...props} />;

export const generateEnumDropdown = (type) => ({ excludeValues = [], ...props }) => {
  const dispatch = useDispatch();
  const options = useSelector(getAllEnum)[type];

  useEffect(() => {
    dispatch(getEnums({ type }));
  }, [dispatch]);

  const excludedOptions = excludeValues.map((value) => String(value).trim().toLowerCase());
  const visibleOptions = (options || []).filter((option) => {
    const value = String(option.value || '').trim().toLowerCase();
    const label = String(option.label || '').trim().toLowerCase();
    return !excludedOptions.includes(value) && !excludedOptions.includes(label);
  });

  return <CommonDropdown options={visibleOptions} loading={!options} {...props} />;
};

export const BuildLocationDropdown = generateEnumDropdown('buildLocations');
export const ChamberTypesDropdown = generateEnumDropdown('chamberTypes');
export const ScrubberTypesDropdown = generateEnumDropdown('scrubberTypes');
export const ChemicalTypesDropdown = generateEnumDropdown('chemicalTypes');
export const OptionsTypesDropdown = generateEnumDropdown('optionsTypes');
export const InteriorLightingTypesDropdown = generateEnumDropdown('interiorLightingTypes');
export const GasSensorTypesDropdown = generateEnumDropdown('gasSensorTypes');
export const ExternalNodeGasSensorTypesDropdown = generateEnumDropdown('externalNodeGasSensorTypes');
export const GasMonitorTypesDropdown = generateEnumDropdown('gasMonitorTypes');
export const DocumentCategoriesTypesDropdown = generateEnumDropdown('documentCategories');
export const AirFilterTypesDropdown = generateEnumDropdown('airFilterTypes');